/* eslint-disable react-refresh/only-export-components -- No refresh, so be it */
import { createContext, useCallback, useContext, useMemo } from "react";
import { useLocalStorage } from "./useLocalStorage";
import { jwtDecode } from "jwt-decode";
import { getApi, type ApiClient } from "./useApi";
import z from "zod";

export interface AuthContextType {
	auth: {
		access_token: string;
		refresh_token: string;
	} | null;
	user: AccessTokenType | null;

	login: (
		username: string,
		password: string,
		refreshTokenDuration: "ephemeral" | "persistent",
	) => Promise<AuthContextType["user"]>;
	logout: () => void;

	getUpdatedToken: () => Promise<string | null>;
	refresh: () => Promise<string | null>;
}

const AuthContext = createContext<AuthContextType | "NOT_INSIDE_PROVIDER">(
	"NOT_INSIDE_PROVIDER",
);

export function AuthProvider({ children }: { readonly children: React.ReactNode; }) {
	const [auth, setAuth] = useLocalStorage<AuthContextType["auth"]>(
		"user",
		null,
	);
	const api = useMemo(() => getApi(), []);

	const login = useCallback(
		async (
			username: string,
			password: string,
			duration: "ephemeral" | "persistent",
		) => {
			const response = await api_login(username, password, duration, api);

			if (response.error) {
				throw response.error;
			} else {
				setAuth(response.data);
				return decodeAccess(response.data.access_token);
			}
		},
		[api, setAuth],
	);

	const refresh = useCallback(async () => {
		if (auth == null) throw new Error("User is not logged in");

		const updated = await api_refreshToken(auth.refresh_token, api);
		if (updated.error) throw new Error(updated.error.code);
		setAuth(updated.data);

		return updated.data.access_token;
	}, [api, auth, setAuth]);

	const logout = useCallback(() => {
		setAuth(null);
	}, [setAuth]);

	const getUpdatedToken = useCallback(async () => {
		if (auth == null) return null;

		const currentAccess = jwtDecode(auth.access_token);

		const now = Math.floor(Date.now() / 1000);
		if (now < (currentAccess.exp ?? Infinity)) return auth.access_token;

		const updated = await api_refreshToken(auth.refresh_token, api);
		if (updated.error) {
			console.error("Error refreshing token:", updated.error);
			setAuth({
				access_token: auth.access_token,
				refresh_token: "EXPIRED",
			});
			return auth.access_token;
		}
		setAuth(updated.data);
		return updated.data.access_token;
	}, [api, auth, setAuth]);

	const userMemo = useMemo(() => decodeAccess(auth?.access_token), [auth]);

	const valueMemo: AuthContextType = useMemo(
		() => ({
			auth: auth,
			user: userMemo,
			login: login,
			refresh: refresh,
			logout: logout,
			getUpdatedToken: getUpdatedToken,
		}),
		[auth, userMemo, login, refresh, logout, getUpdatedToken],
	);

	return (
		<AuthContext.Provider value={valueMemo}>{children}</AuthContext.Provider>
	);
}

export function useAuth() {
	const context = useContext(AuthContext);
	if (context === "NOT_INSIDE_PROVIDER") {
		throw new Error("useAuth must be used within an AuthProvider");
	}
	return context;
}

function api_login(
	username: string,
	password: string,
	duration: "ephemeral" | "persistent",
	client: ApiClient,
) {
	const response = client.request("post", "/v1/Auth/login", {
		body: { username, password, duration },
	});
	return response;
}
function api_refreshToken(refreshToken: string, client: ApiClient) {
	const response = client.request("post", "/v1/Auth/refreshToken", {
		header: { Authorization: "public" },
		body: { refresh_token: refreshToken },
	});

	return response;
}

/**
 * Gets an snapshot of the current authentication values from localStorage.
 */
function decodeAccess(accessToken: string | null | undefined) {
	if (accessToken == null || accessToken === "null") return null;
	return jwtDecode(accessToken) as {
		userId: number;
		userType: "ADMIN" | "EMPRESA";
		tokenType: "ACCESS";
	};
}

export const AccessTokenSchema = z.object({
	userId: z.number(),
	userType: z.enum(["ADMIN", "EMPRESA"]),
	tokenType: z.enum(["ACCESS"]),
});
export const RefreshTokenSchema = z.object({
	userId: z.number(),
	userType: z.enum(["ADMIN", "EMPRESA"]),
	tokenType: z.enum(["REFRESH"]),
});
export type AccessTokenType = z.infer<typeof AccessTokenSchema>;
export type RefreshTokenType = z.infer<typeof RefreshTokenSchema>;
